import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  Calendar, Loader2, RefreshCw, Download, Plus
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "../context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const periodLabels = {
  today: "Today's",
  week: "This Week's",
  month: "This Month's",
  quarter: "This Quarter's",
  year: "This Year's",
  custom: "Custom Period"
};

export default function CashFlowDashboard() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("month");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [summary, setSummary] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [dailyData, setDailyData] = useState([]);

  useEffect(() => {
    if (period !== "custom") {
      loadData();
    } else {
      // When switching to custom, just stop loading state
      setLoading(false);
    }
  }, [period]);

  const loadData = async () => {
    setLoading(true);
    try {
      let summaryUrl = `${API}/cash/summary?period=${period}`;
      if (period === "custom" && startDate && endDate) {
        summaryUrl = `${API}/cash/summary?start_date=${startDate}&end_date=${endDate}`;
      }
      
      const [summaryRes, transactionsRes] = await Promise.all([
        axios.get(summaryUrl),
        axios.get(`${API}/cash/transactions?limit=10`)
      ]);
      
      setSummary(summaryRes.data);
      setRecentTransactions(transactionsRes.data);
      
      if (summaryRes.data.start_date && summaryRes.data.end_date) {
        const dailyRes = await axios.get(
          `${API}/cash/daily-breakdown?start_date=${summaryRes.data.start_date}&end_date=${summaryRes.data.end_date}`
        );
        setDailyData(dailyRes.data);
      }
    } catch (error) {
      console.error("Failed to load cash flow data:", error);
      toast.error("Failed to load cash flow data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric"
    });
  };

  const maxChartValue = Math.max(
    ...dailyData.map(d => Math.max(d.inflows || 0, d.outflows || 0)),
    1
  );

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto" data-testid="cashflow-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Cash Flow</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {periodLabels[period]} overview
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36 h-9 text-sm border-0 bg-muted/50" data-testid="period-select">
              <Calendar className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          
          {period === "custom" && (
            <>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-36 h-9"
                data-testid="cashflow-start-date"
              />
              <span className="text-muted-foreground text-sm">to</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-36 h-9"
                data-testid="cashflow-end-date"
              />
              <Button 
                variant="default" 
                size="sm" 
                onClick={loadData} 
                disabled={loading || !startDate || !endDate} 
                className="h-9"
                data-testid="apply-custom-range-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
              </Button>
            </>
          )}
          
          <Button variant="ghost" size="sm" onClick={loadData} disabled={loading} className="h-9 w-9 p-0">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          
          <Button 
            variant="outline" 
            size="sm" 
            className="h-9" 
            data-testid="download-cashflow-btn"
            onClick={() => {
              // Export cash flow data to CSV
              if (!summary) return;
              const csvContent = [
                ["Cash Flow Report"],
                ["Period", periodLabels[period]],
                ["Start Date", summary.start_date],
                ["End Date", summary.end_date],
                [""],
                ["Summary"],
                ["Opening Balance", summary.opening_balance],
                ["Total Cash In", summary.total_inflows],
                ["Total Cash Out", summary.total_outflows],
                ["Net Cash Flow", summary.net_cash_flow],
                ["Closing Balance", summary.closing_balance],
                [""],
                ["Cash In by Category"],
                ...Object.entries(summary.inflows_by_category || {}).map(([cat, amt]) => [cat, amt]),
                [""],
                ["Cash Out by Category"],
                ...Object.entries(summary.outflows_by_category || {}).map(([cat, amt]) => [cat, amt])
              ].map(row => row.join(",")).join("\n");
              
              const blob = new Blob([csvContent], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `cash-flow-report-${summary.start_date}-to-${summary.end_date}.csv`;
              a.click();
              URL.revokeObjectURL(url);
              toast.success("Cash Flow report downloaded");
            }}
          >
            <Download className="w-4 h-4 mr-1.5" />
            Download Report
          </Button>
        </div>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-4 gap-8 mb-10">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Opening</p>
          <p className="text-2xl font-semibold tabular-nums">
            {formatCurrency(summary?.opening_balance)}
          </p>
        </div>
        
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Cash In</p>
          <p className="text-2xl font-semibold text-emerald-600 tabular-nums">
            +{formatCurrency(summary?.total_inflows)}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {summary?.inflow_count || 0} transactions
          </p>
        </div>
        
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Cash Out</p>
          <p className="text-2xl font-semibold text-red-500 tabular-nums">
            -{formatCurrency(summary?.total_outflows)}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {summary?.outflow_count || 0} transactions
          </p>
        </div>
        
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Closing</p>
          <p className="text-2xl font-semibold text-primary tabular-nums">
            {formatCurrency(summary?.closing_balance)}
          </p>
          <p className={`text-xs mt-0.5 flex items-center gap-1 ${
            (summary?.net_cash_flow || 0) >= 0 ? "text-emerald-600" : "text-red-500"
          }`}>
            {(summary?.net_cash_flow || 0) >= 0 ? (
              <TrendingUp className="w-3 h-3" />
            ) : (
              <TrendingDown className="w-3 h-3" />
            )}
            {formatCurrency(Math.abs(summary?.net_cash_flow || 0))} net
          </p>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-border mb-8" />

      {/* Two Column Layout */}
      <div className="grid grid-cols-3 gap-12">
        {/* Chart Section */}
        <div className="col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-foreground">Daily Trend</h3>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                In
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-400" />
                Out
              </span>
            </div>
          </div>
          
          {/* Simple Bar Chart */}
          <div className="h-40 flex items-end gap-0.5" data-testid="cash-flow-chart">
            {dailyData.length > 0 ? (
              dailyData.slice(-21).map((day, index) => (
                <div key={index} className="flex-1 flex flex-col items-center gap-0.5">
                  <div className="w-full flex gap-px items-end h-32">
                    <div 
                      className="flex-1 bg-emerald-500/80 rounded-sm transition-all duration-300"
                      style={{ height: `${(day.inflows / maxChartValue) * 100}%`, minHeight: day.inflows > 0 ? "2px" : "0" }}
                      title={`In: ${formatCurrency(day.inflows)}`}
                    />
                    <div 
                      className="flex-1 bg-red-400/80 rounded-sm transition-all duration-300"
                      style={{ height: `${(day.outflows / maxChartValue) * 100}%`, minHeight: day.outflows > 0 ? "2px" : "0" }}
                      title={`Out: ${formatCurrency(day.outflows)}`}
                    />
                  </div>
                  {index % 3 === 0 && (
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(day.date).getDate()}
                    </span>
                  )}
                </div>
              ))
            ) : (
              <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
                No data for this period
              </div>
            )}
          </div>
        </div>

        {/* Category Breakdown */}
        <div>
          <h3 className="text-sm font-medium text-foreground mb-4">By Category</h3>
          
          {summary?.inflows_by_category && Object.keys(summary.inflows_by_category).length > 0 && (
            <div className="mb-5">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Income</p>
              <div className="space-y-2">
                {Object.entries(summary.inflows_by_category).map(([category, amount]) => (
                  <div key={category} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{category}</span>
                    <span className="font-medium text-emerald-600 tabular-nums">
                      +{formatCurrency(amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {summary?.outflows_by_category && Object.keys(summary.outflows_by_category).length > 0 && (
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Expenses</p>
              <div className="space-y-2">
                {Object.entries(summary.outflows_by_category).map(([category, amount]) => (
                  <div key={category} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{category}</span>
                    <span className="font-medium text-red-500 tabular-nums">
                      -{formatCurrency(amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {(!summary?.inflows_by_category || Object.keys(summary.inflows_by_category).length === 0) &&
           (!summary?.outflows_by_category || Object.keys(summary.outflows_by_category).length === 0) && (
            <p className="text-sm text-muted-foreground">No transactions</p>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-border my-8" />

      {/* Recent Transactions */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-foreground">Recent Transactions</h3>
          <Link to="/dashboard/cash-flow/ledger" className="text-xs text-primary hover:underline">
            View all
          </Link>
        </div>
        
        {recentTransactions.length > 0 ? (
          <div className="space-y-0">
            {recentTransactions.slice(0, 6).map((trans, index) => (
              <div 
                key={trans.id}
                className={`flex items-center justify-between py-3 ${
                  index !== 0 ? "border-t border-border/50" : ""
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    trans.transaction_type === "income" 
                      ? "bg-emerald-100" 
                      : "bg-red-100"
                  }`}>
                    {trans.transaction_type === "income" ? (
                      <ArrowUpRight className="w-3 h-3 text-emerald-600" />
                    ) : (
                      <ArrowDownRight className="w-3 h-3 text-red-500" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{trans.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {trans.category} · {formatDate(trans.cash_date)}
                    </p>
                  </div>
                </div>
                <p className={`text-sm font-medium tabular-nums ${
                  trans.transaction_type === "income" ? "text-emerald-600" : "text-red-500"
                }`}>
                  {trans.transaction_type === "income" ? "+" : "-"}
                  {formatCurrency(trans.amount)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-sm text-muted-foreground mb-3">No transactions yet</p>
            <Link to="/dashboard/cash-flow/ledger">
              <Button size="sm" variant="outline">
                <Plus className="w-4 h-4 mr-1.5" />
                Add First Entry
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
